import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  isAttendeeOption,
  isAttendeeOptionArray,
  isPlaceholderOption,
} from '@/helpers/other';
import { AttendeeOption } from '@/ui/select/async/components/option/attendee-option';
import type { AsyncSelectOptionProps } from '@/ui/select/async/components/option/option';
import { PlaceholderOption } from '@/ui/select/async/components/option/placeholder-option';

import { useAttendeesOptions } from '../hooks/use-attendees-options';
import type { MeetingFormFields } from '../hooks/use-meeting-form';
import { FormSelect } from '../ui/form-select';
import { WithLabel } from '../ui/with-label';
import type { MeetingTabContext } from '../meetings-tab';

export const AttendeesSelect = ({
  context,
}: {
  context: MeetingTabContext;
}) => {
  const { t } = useTranslation('default');
  const { control, watch } = useFormContext<MeetingFormFields>();

  const { setOptions } = useAttendeesOptions({
    context,
  });

  const relatedCompanyId = watch('relatedCompanyId');
  const relatedTeamId = watch('relatedTeamId');

  return (
    <WithLabel
      name="meeting-attendees"
      labelText={t`actionPanel.meetings.attendees`}
    >
      <Controller
        control={control}
        name="attendees"
        render={({ field: { onChange, value } }) => (
          <FormSelect
            // https://stackoverflow.com/questions/54107238/clear-cached-options-on-async-select
            key={JSON.stringify({ relatedCompanyId, relatedTeamId })}
            isMulti
            isOptionDisabled={option => isPlaceholderOption(option)}
            setOptions={setOptions}
            value={value}
            components={{
              Option: AttendeeSelectOption,
            }}
            onChange={newAttendees => {
              if (!isAttendeeOptionArray(newAttendees)) return;

              onChange(newAttendees);
            }}
          />
        )}
      />
    </WithLabel>
  );
};

function AttendeeSelectOption({ data, ...props }: AsyncSelectOptionProps) {
  if (isAttendeeOption(data)) {
    return <AttendeeOption {...props} data={data} />;
  }

  if (isPlaceholderOption(data)) {
    return <PlaceholderOption {...props} data={data} />;
  }

  return null;
}
